import {Hotkey, IconButton} from '@wandb/components';
import {isMac} from '@wandb/weave/common/util/browser';
import React from 'react';
import TimeAgo, {type Formatter} from 'react-timeago';
import {twMerge} from 'tailwind-merge';

import {METICULOUS_IGNORE} from '../../../services/meticulous/privacy';
import {DD_PRIVACY_ALLOW} from '../../../util/datadog/privacy';
import {useUndoRedo} from '../hooks/useUndoRedo';
import {useViewBarContext} from '../NamedWorkspaceViewBarContext';

const WORKSPACE_TIMEAGO_FORMATTER: Formatter = (
  value,
  unit,
  suffix,
  epochMilliseconds,
  nextFormatter,
  now
) =>
  unit === 'second'
    ? 'just now'
    : nextFormatter(value, unit, suffix, epochMilliseconds, nextFormatter, now);

export const NamedWorkspaceUndoRedo = () => {
  const {currentView, undo, redo} = useUndoRedo();

  if (currentView == null) {
    return null;
  }

  return (
    <div className="flex gap-2">
      <IconButton
        data-dd-action-name="Undo"
        data-dd-privacy={DD_PRIVACY_ALLOW}
        data-test="nw-undo-button"
        icon="undo"
        variant="secondary"
        aria-label="Undo last action"
        isDisabled={!undo}
        onPress={() => undo?.(false)}
        tooltip={{
          placement: 'bottom',
          children: (
            <>
              Undo <Hotkey name="Undo" keys={['Primary', 'KeyZ']} />
            </>
          ),
        }}
      />
      <IconButton
        data-dd-action-name="Redo"
        data-dd-privacy={DD_PRIVACY_ALLOW}
        data-test="nw-redo-button"
        icon="redo"
        variant="secondary"
        aria-label="Redo last action"
        isDisabled={!redo}
        onPress={() => redo?.(false)}
        tooltip={{
          placement: 'bottom',
          children: (
            <>
              {/*TODO: This should be normalized for all platforms when the new input system is implemented*/}
              Redo{' '}
              <Hotkey
                name="Redo"
                keys={
                  isMac ? ['Primary', 'Shift', 'KeyZ'] : ['Primary', 'KeyY']
                }
              />
            </>
          ),
        }}
      />
    </div>
  );
};

export const NamedWorkspaceTimestamp = ({className}: {className: string}) => {
  const {currentView, currentWorkspaceNwId} = useViewBarContext();

  if (currentView == null) {
    return null;
  }

  const isPersonalWorkspace =
    currentWorkspaceNwId?.nwType === 'personal workspace';

  if (currentView.saving) {
    return (
      <p
        data-dd-action-name="Workspace save status"
        data-dd-privacy={DD_PRIVACY_ALLOW}
        className={twMerge(
          isPersonalWorkspace && METICULOUS_IGNORE, // autosave timing cannot be guaranteed
          'text-base font-normal leading-[22px] text-wb-text-tertiary',
          className
        )}>
        Saving...
      </p>
    );
  }

  const workspaceUpdatedAt = currentView.updatedAt;

  if (currentView.id == null || !workspaceUpdatedAt) {
    return (
      <p
        data-dd-action-name="Workspace save status"
        data-dd-privacy={DD_PRIVACY_ALLOW}
        className={twMerge(
          'text-base font-normal leading-[22px] text-wb-text-tertiary',
          className
        )}>
        Changes saved automatically
      </p>
    );
  }

  return (
    // The <time> from react-timeago carries a dateTime attr, so without a stable
    // name RUM derives the action from the timestamp ("Saved just now" / the ISO
    // string). Name the whole status so every save state is one RUM action.
    <div
      data-dd-action-name="Workspace save status"
      data-dd-privacy={DD_PRIVACY_ALLOW}
      className={twMerge(
        METICULOUS_IGNORE, // time-ago value changes depending on when the test is run
        'text-base font-normal leading-[22px] text-wb-text-tertiary',
        className
      )}>
      <p>
        Saved{' '}
        <span>
          <TimeAgo
            minPeriod={10}
            formatter={WORKSPACE_TIMEAGO_FORMATTER}
            date={workspaceUpdatedAt}
          />
        </span>
      </p>
    </div>
  );
};
